import Api from '@sendsprint/api-types';
import { IMAGE_SUPPORTED_FORMATS, TWO_MB } from '@src/constants';
import { phoneSchemaFactory, Shape } from '@src/validations';
import * as yup from 'yup';
import { BusinessInformationFormData } from '../components/businessInformationStep';
import { getSentenceWords } from '@src/utils/getSentenceWords';

interface GetBusinessInformationOptions {
  businessCountry: Api.Model.CountryInitials | undefined;
}

const addressSchema = yup.object().shape({
  id: yup.string().required('ID is required'),
  address: yup.string().when('show', (show: boolean) => {
    return show ? yup.string().required('Please enter address') : yup.string();
  }),
  city: yup.string().when('show', (show: boolean) => {
    return show ? yup.string().required('Please enter city') : yup.string();
  }),
  state: yup.string().when('show', (show: boolean) => {
    return show ? yup.string().required('Please enter state') : yup.string();
  }),
  zipCode: yup.string().when('show', (show: boolean) => {
    return show ? yup.string().required('Please enter zip code') : yup.string();
  }),
  registeredAddressEvidence: yup
    .mixed()
    .nullable()
    .when('show', (show: boolean) => {
      return show
        ? yup
            .mixed()
            .nullable()
            .required('Please add document')
            .test(
              'FILE_FORMAT',
              'Please check the format of the files selected. The supported formats are jpeg, jpg or png.',
              (value) => {
                if (typeof value === 'string') {
                  return true;
                }

                if (value) {
                  if (!IMAGE_SUPPORTED_FORMATS.includes(value?.type)) {
                    return false;
                  }
                }

                return true;
              }
            )
            .test('FILE_SIZE', 'File too big. Maximum of 2mb', (value) => {
              if (typeof value === 'string') {
                return true;
              }

              if (value) {
                if (value.size > TWO_MB) {
                  return false;
                }
              }

              return true;
            })
        : yup.mixed().nullable();
    })
});

export const getBusinessInformationSchema = ({ businessCountry }: GetBusinessInformationOptions) =>
  yup.object().shape<Shape<BusinessInformationFormData>>({
    typeOfBusiness: yup.string().required('Please select business type'),
    companyRegistrationNumber: yup.string().required('Please enter company registration number'),
    taxIdentificationNumber: yup.string().required('Please enter tax identification number'),
    einNumber: yup.string().required('Please enter ein number'),
    transactionValues: yup.string().required('Please select transaction value'),
    pciDssLevel: yup.string(),
    fraudToolsInPlace: yup.string().required('Please enter fraud tools in place'),
    useRegisteredAddress: yup.boolean(),
    businessDescription: yup
      .string()
      .required('Please enter business description')
      .test('NUMBER_OF_WORDS', 'Business description should be more than 10 words', (value) => {
        if (value) {
          const numberOfWords = getSentenceWords(value);

          if (numberOfWords && numberOfWords < 10) {
            return false;
          }
        }

        return true;
      }),
    businessEmailAddress: yup
      .string()
      .email('Please enter a valid email')
      .required('Please enter business email address'),
    businessIndustry: yup.string().required('Please select business industry'),
    businessName: yup.string().required('Please enter business name'),
    businessPhoneNumber: phoneSchemaFactory({
      countryCodeOrPath: businessCountry || '',
      required: true,
      numbersOnly: true
    }),
    logo: yup
      .mixed()
      .nullable()
      .required('Please add logo')
      .test(
        'FILE_FORMAT',
        'Please check the format of the files selected. The supported formats are jpeg, jpg or png.',
        (value) => {
          if (typeof value === 'string') {
            return true;
          }

          if (value) {
            if (!IMAGE_SUPPORTED_FORMATS.includes(value?.type)) {
              return false;
            }
          }

          return true;
        }
      )
      .test('FILE_SIZE', 'File too big. Maximum of 2mb', (value) => {
        if (value) {
          if (typeof value === 'string') {
            return true;
          }

          if (value.size > TWO_MB) {
            return false;
          }
        }

        return true;
      }),
    tradeName: yup.string(),
    companyWebsiteUrl: yup.string().required('Please enter company website url'),
    addressInfo: yup
      .array()
      .of(addressSchema)
      .min(1, 'At least one address is required')
      .required('Address information is required')
  });
